import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../Firebaseconfig';
import './NotificationSidebar.css';
import { FaTimes, FaCheck, FaCheckCircle, FaClock, FaBoxOpen, FaInfoCircle } from 'react-icons/fa';

const normalizeOrderNotifications = (items) => {
  const byOrder = new Map();

  items.forEach((item) => {
    if (!item.orderId) return;

    const normalized = {
      ...item,
      id: item.orderId,
      timestamp: item.timestamp || 0
    };
    const existing = byOrder.get(item.orderId);

    if (!existing || normalized.timestamp >= existing.timestamp) {
      byOrder.set(item.orderId, normalized);
    }
  });

  return Array.from(byOrder.values()).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
};

export default function NotificationSidebar({ isOpen, onClose, user, onUnreadCountChange }) {
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState('Updates');
  const [loading, setLoading] = useState(true);

  // Status message mapping
  const getStatusMessage = (status) => {
    switch (status) {
      case 'pending': return 'Your order is pending confirmation.';
      case 'accepted': return 'Your order has been accepted by the restaurant.';
      case 'ready': return 'Your order is ready for pickup.';
      case 'pickingup': return 'A courier is picking up your order.';
      case 'delivering': return 'Your order is on the way!';
      case 'delivered': 
      case 'completed': return 'Your order has been delivered.';
      case 'cancelled': return 'Your order was cancelled.';
      default: return `Order status updated to ${status}.`;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <FaClock className="noti-icon pending" />;
      case 'accepted': return <FaCheckCircle className="noti-icon accepted" />;
      case 'ready': return <FaBoxOpen className="noti-icon ready" />;
      case 'delivering':
      case 'pickingup': return <FaBoxOpen className="noti-icon delivering" />;
      case 'delivered': 
      case 'completed': return <FaCheck className="noti-icon completed" />;
      case 'cancelled': return <FaTimes className="noti-icon cancelled" />;
      default: return <FaInfoCircle className="noti-icon default" />;
    }
  };

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setLoading(false);
      onUnreadCountChange(0);
      return;
    }

    // Load persisted notifications and seen statuses
    const storageKeyNotifs = `notifs_${user.uid}`;
    const storageKeySeen = `seen_status_${user.uid}`;
    let persistedNotifs = normalizeOrderNotifications(JSON.parse(localStorage.getItem(storageKeyNotifs) || '[]'));
    let seenStatuses = JSON.parse(localStorage.getItem(storageKeySeen) || '{}');

    setNotifications(persistedNotifs);
    localStorage.setItem(storageKeyNotifs, JSON.stringify(persistedNotifs));
    setLoading(true);

    const q = query(
      collection(db, 'orders'),
      where('customerId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    let isFirstSnapshot = true;

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let updated = false;
      let newNotifs = [...persistedNotifs];

      snapshot.docs.forEach((doc) => {
        const order = doc.data();
        const orderId = doc.id;
        const currentStatus = order.status;

        // Ignore if we don't have a status or it's the same as seen before
        if (!currentStatus || seenStatuses[orderId] === currentStatus) return;

        // Skip generating notifications for completely unseen orders on the very first load.
        // This prevents spamming notifications for old past orders when a user logs in.
        if (isFirstSnapshot && !seenStatuses[orderId]) {
          seenStatuses[orderId] = currentStatus;
          updated = true;
          return;
        }
        
        const notif = {
          id: orderId,
          orderId,
          orderName: order.productName || 'Your Order',
          companyName: order.companyName || '',
          status: currentStatus,
          message: getStatusMessage(currentStatus),
          timestamp: Date.now(),
          read: false
        };

        const existingIndex = newNotifs.findIndex(n => n.orderId === orderId);

        if (existingIndex === -1) {
          newNotifs.unshift(notif);
        } else {
          newNotifs = [
            { ...newNotifs[existingIndex], ...notif },
            ...newNotifs.slice(0, existingIndex),
            ...newNotifs.slice(existingIndex + 1)
          ];
        }

        seenStatuses[orderId] = currentStatus;
        updated = true;
      });

      isFirstSnapshot = false;

      if (updated) {
        // keep only latest 50 notifications
        if (newNotifs.length > 50) newNotifs = newNotifs.slice(0, 50);
        
        persistedNotifs = newNotifs;
        setNotifications(newNotifs);
        localStorage.setItem(storageKeyNotifs, JSON.stringify(newNotifs));
        localStorage.setItem(storageKeySeen, JSON.stringify(seenStatuses));
      }
      
      const unread = persistedNotifs.filter(n => !n.read).length;
      onUnreadCountChange(unread);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching orders for notifications:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, onUnreadCountChange]);

  // Recalculate unread count if notifications change (like marking read)
  useEffect(() => {
    const unread = notifications.filter(n => !n.read).length;
    onUnreadCountChange(unread);
  }, [notifications, onUnreadCountChange]);

  const markAllAsRead = () => {
    if (!user) return;
    const updated = notifications.map(n => ({ ...n, read: true }));
    setNotifications(updated);
    localStorage.setItem(`notifs_${user.uid}`, JSON.stringify(updated));
  };

  const markAsRead = (id) => {
    if (!user) return;
    const updated = notifications.map(n => n.id === id ? { ...n, read: true } : n);
    setNotifications(updated);
    localStorage.setItem(`notifs_${user.uid}`, JSON.stringify(updated));
  };

  const removeNotification = (e, id) => {
    e.stopPropagation(); // prevent markAsRead from firing
    if (!user) return;
    const updated = notifications.filter(n => n.id !== id);
    setNotifications(updated);
    localStorage.setItem(`notifs_${user.uid}`, JSON.stringify(updated));
  };

  const getRelativeTime = (timestamp) => {
    const diff = Math.floor((Date.now() - timestamp) / 1000); // in seconds
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={onClose}></div>}
      <div className={`notification-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>Notifications</h2>
          <button className="close-btn" onClick={onClose}><FaTimes /></button>
        </div>
        
        <div className="sidebar-tabs">
          <div className="tabs-left">
            <span className={`tab ${activeTab === 'Updates' ? 'active' : ''}`} onClick={() => setActiveTab('Updates')}>Updates</span>
            <span className={`tab ${activeTab === 'Mentions' ? 'active' : ''}`} onClick={() => setActiveTab('Mentions')}>Mentions</span>
          </div>
          <span className="mark-read" onClick={markAllAsRead}>Mark all as read</span>
        </div>

        <div className="notification-list">
          {loading && notifications.length === 0 ? (
            <div className="notification-empty">
              <FaBoxOpen className="notification-empty-icon" />
              <h4>Loading notifications...</h4>
            </div>
          ) : notifications.length === 0 ? (
            <div className="notification-empty">
              <FaBoxOpen className="notification-empty-icon" />
              <h4>No notifications yet.</h4>
              <p>When you have updates, they will appear here.</p>
            </div>
          ) : (
            notifications.map((notif) => (
              <div 
                key={notif.id} 
                className={`notification-item ${notif.read ? 'read' : 'unread'}`}
                onClick={() => markAsRead(notif.id)}
              >
                <div className="notification-content">
                  <div className={`notification-icon ${notif.status === 'pending' || notif.status === 'accepted' ? 'offer' : notif.status === 'cancelled' ? 'info' : 'order'}`}>
                    {getStatusIcon(notif.status)}
                  </div>
                  <div className="notification-body">
                    <h4 className="notification-title">{notif.companyName ? `${notif.companyName} - ${notif.orderName}` : notif.orderName}</h4>
                    <p className="notification-message">{notif.message}</p>
                    <span className="notification-time">{getRelativeTime(notif.timestamp)}</span>
                  </div>
                </div>
                <button 
                  className="remove-noti-btn"
                  onClick={(e) => removeNotification(e, notif.id)}
                  title="Remove notification"
                >
                  <FaTimes />
                </button>
              </div>
            ))
          )}
        </div>
        
        <div className="sidebar-footer">
          <button className="view-all-btn">View All Activity</button>
        </div>
      </div>
    </>
  );
}
